import { useCallback, useEffect, useState } from "react";
import {
  FilePdf, UploadSimple, CheckCircle, XCircle, MinusCircle, Power,
  ShieldCheck, DownloadSimple, HardDrives, ArrowClockwise,
} from "@phosphor-icons/react";
import AdminShell from "@/pages/admin/AdminShell";
import { Section } from "@/pages/Shell";
import { formatError } from "@/lib/api";
import {
  listSalesAssets, getSalesAssetsStorage, activateSalesAsset,
  deactivateSalesAsset, verifySalesAsset, salesAssetDownloadUrl,
  uploadSalesAsset,
} from "@/lib/salesAssetsApi";

/**
 * Activos comerciales — los documentos que el sistema puede ENTREGAR a un
 * lead. Hoy, el dossier en español.
 *
 * Lo que la pantalla tiene que dejar claro de un vistazo, porque es lo que
 * se confunde:
 *   - cuál es la versión ACTIVA (la única que puede salir) y cuáles son
 *     historia;
 *   - que subir no publica: una versión nueva nace inactiva y hay que
 *     activarla a propósito;
 *   - si lo que hay en disco sigue siendo lo que se subió. Eso no se deduce
 *     del listado: lo dice «Verificar», que recalcula la huella y además
 *     descarga el PDF por su URL pública sin sesión, como lo hará WhatsApp.
 */

const TAP = "min-h-[44px]";

const KIND_LABELS = { dossier: "Dossier" };
const LANGUAGE_LABELS = { es: "Español" };

const CHECK_LABELS = [
  ["exists", "Registrada en el catálogo"],
  ["active", "Activa"],
  ["mime_ok", "Tipo PDF"],
  ["size_ok", "Tamaño admitido por WhatsApp"],
  ["object_present", "Fichero en disco"],
  ["hash_ok", "Huella sha256 íntegra"],
  ["link_ok", "URL pública https"],
  ["download_ok", "Descarga anónima real"],
];

const REASON_LABELS = {
  asset_missing: "La versión no existe.",
  asset_inactive: "La versión no está activa.",
  asset_kind_unknown: "Tipo o idioma no admitido.",
  asset_mime_invalid: "No es un PDF.",
  asset_too_large: "Supera el máximo que WhatsApp admite.",
  asset_object_missing: "Falta el fichero en disco.",
  asset_size_mismatch: "El fichero en disco no mide lo registrado.",
  asset_hash_mismatch: "El fichero en disco ha cambiado: su huella no coincide.",
  asset_link_unavailable: "La URL pública del servidor no es https.",
  asset_check_failed: "No se pudo comprobar.",
  anonymous_download_failed: "La descarga anónima por la URL pública falló.",
};

const DOWNLOAD_ERRORS = {
  not_deliverable: "No se intentó: la versión no es entregable.",
  link_create_failed: "No se pudo crear el enlace de prueba.",
  transport_error: "No se pudo conectar con la URL pública.",
  head_failed: "La petición HEAD no respondió 200.",
  download_failed: "La descarga no respondió 200.",
  size_mismatch: "Lo descargado no mide lo registrado.",
  hash_mismatch: "Lo descargado no tiene la huella registrada.",
};

const PHASE_LABELS = {
  hashing: "Calculando la huella…",
  uploading: "Subiendo…",
  completing: "Verificando en el servidor…",
};

export function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return "—";
  if (v < 1000) return `${v} B`;
  const fmt = (x) => x.toLocaleString("es-ES", { maximumFractionDigits: 1 });
  if (v < 1_000_000) return `${fmt(v / 1000)} KB`;
  if (v < 1_000_000_000) return `${fmt(v / 1_000_000)} MB`;
  return `${fmt(v / 1_000_000_000)} GB`;
}

/**
 * El error de activar, con el PORQUÉ. El 409 `asset_not_deliverable` trae
 * en `reason` la misma comprobación que falla al verificar; sin ella el
 * admin solo lee «no se puede entregar» y tiene que ir a Verificar para
 * saber qué arreglar.
 */
function activationError(e) {
  const base = formatError(e);
  const detail = e?.response?.data?.detail;
  const reason = e?.response?.status === 409 && detail && typeof detail === "object"
    ? detail.reason : null;
  if (!reason) return base;
  const motivo = REASON_LABELS[reason] || reason;
  return base.includes(motivo) ? base : `${base} Motivo: ${motivo}`;
}

const fmtDate = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");
const kindLabel = (k) => KIND_LABELS[k] || k;
const languageLabel = (l) => LANGUAGE_LABELS[l] || l;

function Pill({ children, className = "", ...rest }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-tight ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
}

function CheckIcon({ value }) {
  if (value === true) return <CheckCircle size={14} className="text-emerald-600" weight="fill" />;
  if (value === false) return <XCircle size={14} className="text-rose-600" weight="fill" />;
  return <MinusCircle size={14} className="text-stone-400" />;
}

function VerifyResult({ result, assetId }) {
  const d = result.download || {};
  return (
    <div
      className={`mt-3 rounded-lg border p-3 text-xs ${result.ok
        ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60"}`}
      data-testid={`sa-verify-${assetId}`}
    >
      <div className="font-medium mb-2" data-testid={`sa-verify-summary-${assetId}`}>
        {result.ok
          ? "Entregable: el PDF está íntegro y se descarga sin sesión por su URL pública."
          : (REASON_LABELS[result.reason] || result.reason || "No entregable.")}
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {CHECK_LABELS.map(([key, label]) => (
          <li key={key} className="flex items-center gap-1.5 min-w-0"
              data-testid={`sa-check-${assetId}-${key}`}
              data-state={result.checks?.[key] === true ? "ok"
                : result.checks?.[key] === false ? "fail" : "unknown"}>
            <CheckIcon value={result.checks?.[key]} />
            <span className="break-words">{label}</span>
          </li>
        ))}
      </ul>
      {d.attempted ? (
        <p className="mt-2 text-stone-600 break-words" data-testid={`sa-verify-download-${assetId}`}>
          HEAD {d.head_status ?? "—"} · GET {d.status_code ?? "—"}
          {d.bytes != null ? ` · ${formatBytes(d.bytes)} recibidos` : ""}
          {d.error ? ` · ${DOWNLOAD_ERRORS[d.error] || d.error}` : ""}
        </p>
      ) : d.error ? (
        <p className="mt-2 text-stone-600 break-words" data-testid={`sa-verify-download-${assetId}`}>
          {DOWNLOAD_ERRORS[d.error] || d.error}
        </p>
      ) : null}
    </div>
  );
}

function AssetRow({ asset, busy, verify, onActivate, onDeactivate, onVerify }) {
  const id = asset.asset_id;
  return (
    <li className="border-b border-stone-200/70 last:border-b-0 py-3 px-3 sm:px-4"
        data-testid={`sa-row-${id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            {asset.active ? (
              <Pill className="bg-emerald-50 text-emerald-700 border-emerald-200"
                    data-testid={`sa-active-${id}`}>
                Activa
              </Pill>
            ) : (
              <Pill className="bg-stone-100 text-stone-500 border-stone-200"
                    data-testid={`sa-inactive-${id}`}>
                Inactiva
              </Pill>
            )}
            <Pill className="bg-stone-50 text-stone-600 border-stone-200"
                  data-testid={`sa-kind-${id}`}>
              {kindLabel(asset.kind)} · {languageLabel(asset.language)}
            </Pill>
            <span className="text-xs text-stone-500" data-testid={`sa-version-${id}`}>
              v{asset.version}
            </span>
          </div>
          <div className="font-medium text-sm break-words inline-flex items-center gap-1.5">
            <FilePdf size={15} className="text-stone-500 shrink-0" />
            <span data-testid={`sa-filename-${id}`}>{asset.filename}</span>
          </div>
          <div className="text-[11px] text-stone-500 mt-1 break-words">
            <span data-testid={`sa-size-${id}`}>{formatBytes(asset.size_bytes)}</span>
            {" · "}
            <span className="font-mono" title={asset.sha256} data-testid={`sa-sha-${id}`}>
              {String(asset.sha256 || "").slice(0, 12)}
            </span>
            {" · subida "}<span data-testid={`sa-created-${id}`}>{fmtDate(asset.created_at)}</span>
            {asset.activated_at ? (
              <>{" · activada "}<span data-testid={`sa-activated-${id}`}>{fmtDate(asset.activated_at)}</span></>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 shrink-0">
          {asset.active ? (
            <button type="button" onClick={() => onDeactivate(asset)} disabled={busy}
              className={`dv-btn dv-btn-outline text-xs inline-flex items-center gap-1 ${TAP} sm:min-h-0 disabled:opacity-40`}
              data-testid={`sa-deactivate-${id}`}>
              <Power size={13} /> Desactivar
            </button>
          ) : (
            <button type="button" onClick={() => onActivate(asset)} disabled={busy}
              className={`dv-btn dv-btn-primary text-xs inline-flex items-center gap-1 ${TAP} sm:min-h-0 disabled:opacity-40`}
              data-testid={`sa-activate-${id}`}>
              <CheckCircle size={13} /> Activar
            </button>
          )}
          <button type="button" onClick={() => onVerify(asset)} disabled={busy}
            className={`dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 ${TAP} sm:min-h-0 disabled:opacity-40`}
            data-testid={`sa-verify-btn-${id}`}>
            <ShieldCheck size={13} /> Verificar
          </button>
          <a href={salesAssetDownloadUrl(id)}
            className={`dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 ${TAP} sm:min-h-0`}
            data-testid={`sa-download-${id}`}>
            <DownloadSimple size={13} /> Descargar
          </a>
        </div>
      </div>
      {verify ? <VerifyResult result={verify} assetId={id} /> : null}
    </li>
  );
}

function StorageCard({ storage, error, onRefresh, busy }) {
  return (
    <div className="dv-card" data-testid="sa-storage">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="inline-flex items-center gap-1.5 text-sm font-medium">
          <HardDrives size={15} /> Disco del servidor
        </div>
        <button type="button" onClick={onRefresh} disabled={busy}
          className={`dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 ${TAP} sm:min-h-0 disabled:opacity-40`}
          data-testid="sa-storage-refresh">
          <ArrowClockwise size={13} /> Comprobar
        </button>
      </div>
      {error ? (
        <p className="text-sm text-rose-700" data-testid="sa-storage-error">{error}</p>
      ) : !storage ? (
        <p className="text-sm text-stone-500">Comprobando…</p>
      ) : (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div className="min-w-0 sm:col-span-2">
            <dt className="text-[10px] uppercase tracking-widest text-stone-500">Directorio</dt>
            <dd className="font-mono text-xs break-all" data-testid="sa-storage-dir">{storage.directory}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-stone-500">Escritura</dt>
            <dd data-testid="sa-storage-writable"
                className={storage.writable ? "text-emerald-700" : "text-rose-700"}>
              {storage.writable
                ? "Se puede escribir (probado)"
                : `No se puede escribir${storage.write_error ? ` (${storage.write_error})` : ""}`}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-stone-500">Espacio libre</dt>
            <dd data-testid="sa-storage-free">{storage.free_bytes != null ? formatBytes(storage.free_bytes) : "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-stone-500">Ficheros</dt>
            <dd data-testid="sa-storage-objects">
              {storage.objects?.count ?? 0} · {formatBytes(storage.objects?.bytes ?? 0)}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-stone-500">Subidas a medias</dt>
            <dd data-testid="sa-storage-partials">{storage.partial_uploads?.count ?? 0}</dd>
          </div>
          <div className="min-w-0 sm:col-span-2">
            <dt className="text-[10px] uppercase tracking-widest text-stone-500">Versiones sin fichero</dt>
            <dd data-testid="sa-storage-missing"
                className={storage.missing?.length ? "text-rose-700 break-words" : "break-words"}>
              {storage.missing?.length
                ? storage.missing.map((m) => `${kindLabel(m.kind)} ${String(m.language || "").toUpperCase()} v${m.version}${m.active ? " (activa)" : ""}`).join(" · ")
                : "Ninguna"}
            </dd>
          </div>
          <div className="min-w-0 sm:col-span-2">
            <dt className="text-[10px] uppercase tracking-widest text-stone-500">Ficheros huérfanos</dt>
            <dd data-testid="sa-storage-orphans" className="font-mono text-xs break-all">
              {storage.orphans?.length
                ? storage.orphans.map((o) => String(o).slice(0, 12)).join(" · ")
                : "Ninguno"}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function UploadForm({ kinds, languages, onUploaded, onError }) {
  const [kind, setKind] = useState("");
  const [language, setLanguage] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [inputKey, setInputKey] = useState(0);

  const effKind = kind || kinds[0] || "";
  const effLanguage = language || languages[0] || "";
  const uploading = progress != null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file || uploading) return;
    setProgress({ phase: "hashing", sent: 0, total: file.size });
    try {
      const res = await uploadSalesAsset({
        file, kind: effKind, language: effLanguage, onProgress: setProgress,
      });
      setFile(null);
      setInputKey((k) => k + 1);
      await onUploaded(res);
    } catch (err) {
      onError(formatError(err));
    } finally {
      setProgress(null);
    }
  };

  const pct = progress && progress.total
    ? Math.min(100, Math.round((progress.sent / progress.total) * 100)) : 0;

  return (
    <form className="dv-card" onSubmit={onSubmit} data-testid="sa-upload-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <label className="text-xs min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Tipo</div>
          <select className="dv-input text-sm w-full" value={effKind}
                  onChange={(e) => setKind(e.target.value)} disabled={uploading}
                  data-testid="sa-upload-kind">
            {kinds.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
          </select>
        </label>
        <label className="text-xs min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Idioma</div>
          <select className="dv-input text-sm w-full" value={effLanguage}
                  onChange={(e) => setLanguage(e.target.value)} disabled={uploading}
                  data-testid="sa-upload-language">
            {languages.map((l) => <option key={l} value={l}>{languageLabel(l)}</option>)}
          </select>
        </label>
        <label className="text-xs min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">PDF</div>
          <input key={inputKey} type="file" accept="application/pdf,.pdf"
                 className="dv-input text-sm w-full" disabled={uploading}
                 onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                 data-testid="sa-upload-file" />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <button type="submit" disabled={!file || uploading || !effKind || !effLanguage}
          className={`dv-btn dv-btn-primary text-sm inline-flex items-center gap-1.5 ${TAP} sm:min-h-0 disabled:opacity-40`}
          data-testid="sa-upload-submit">
          <UploadSimple size={14} /> Subir versión
        </button>
        {file && !uploading ? (
          <span className="text-xs text-stone-500 break-words" data-testid="sa-upload-selected">
            {file.name} · {formatBytes(file.size)}
          </span>
        ) : null}
      </div>
      {uploading ? (
        <div className="mt-3" data-testid="sa-upload-progress">
          <div className="text-xs text-stone-600 mb-1">
            {PHASE_LABELS[progress.phase] || "Subiendo…"}
            {progress.phase === "uploading" ? ` ${pct} %` : ""}
          </div>
          <div className="h-2 rounded-full bg-stone-200 overflow-hidden"
               role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <div className="h-full bg-stone-800 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      ) : null}
      <p className="text-[11px] text-stone-500 mt-2">
        Una versión nueva nace INACTIVA: no se entrega a nadie hasta que la
        actives. Al activarla, la anterior del mismo tipo e idioma se desactiva.
      </p>
    </form>
  );
}

export default function SalesAssetsPage() {
  const [items, setItems] = useState([]);
  const [kinds, setKinds] = useState(["dossier"]);
  const [languages, setLanguages] = useState(["es"]);
  const [storage, setStorage] = useState(null);
  const [storageError, setStorageError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [verifications, setVerifications] = useState({});

  const reload = useCallback(async () => {
    try {
      const res = await listSalesAssets();
      setItems(res.items);
      if (res.kinds.length) setKinds(res.kinds);
      if (res.languages.length) setLanguages(res.languages);
      setError("");
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadStorage = useCallback(async () => {
    setBusyId("storage");
    try {
      setStorage(await getSalesAssetsStorage());
      setStorageError("");
    } catch (e) {
      setStorageError(formatError(e));
    } finally {
      setBusyId((b) => (b === "storage" ? null : b));
    }
  }, []);

  useEffect(() => { reload(); reloadStorage(); }, [reload, reloadStorage]);

  const run = async (id, fn, msg, describeError = formatError) => {
    setBusyId(id);
    setNotice("");
    setError("");
    try {
      await fn();
      if (msg) setNotice(msg);
      await reload();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusyId(null);
    }
  };

  // Activar o desactivar una versión cambia también la otra del mismo tipo
  // e idioma, así que TODAS las verificaciones pintadas quedan viejas: una
  // caja verde que dice «Activa» sobre la versión que acaba de apagarse
  // miente. Se borran todas y se vuelve a verificar a propósito.
  const onActivate = (a) => run(a.asset_id, async () => {
    await activateSalesAsset(a.asset_id);
    setVerifications({});
  }, `Versión ${a.version} activada. Es la única de su tipo e idioma que se puede entregar.`,
  activationError);

  const onDeactivate = (a) => run(a.asset_id, async () => {
    await deactivateSalesAsset(a.asset_id);
    setVerifications({});
  }, `Versión ${a.version} desactivada. No queda ninguna activa de su tipo e idioma.`);

  const onVerify = (a) => run(a.asset_id, async () => {
    const res = await verifySalesAsset(a.asset_id);
    setVerifications((v) => ({ ...v, [a.asset_id]: res }));
  }, null);

  const onUploaded = async (res) => {
    setError("");
    setNotice(res?.created === false
      ? `Esta subida ya estaba cerrada como versión ${res?.asset?.version}.`
      : `Versión ${res?.asset?.version} subida. Queda inactiva hasta que la actives.`);
    await reload();
    await reloadStorage();
  };

  const onUploadError = (msg) => {
    setNotice("");
    setError(msg);
  };

  return (
    <AdminShell>
      <div className="mb-6 fade-up">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">IA comercial</div>
        <h1 className="font-serif-dv text-4xl">Activos comerciales.</h1>
        <p className="text-stone-500 mt-2">
          Los documentos que el sistema puede entregar a un lead. Solo sale la
          versión activa de cada tipo e idioma, y solo si su fichero sigue
          íntegro en el servidor.
        </p>
      </div>

      {error && (
        <div className="dv-card border-rose-200 bg-rose-50 text-rose-800 text-sm mb-4 break-words"
             data-testid="sa-error">
          {error}
        </div>
      )}
      {notice && (
        <div className="dv-card border-emerald-200 bg-emerald-50 text-emerald-800 text-sm mb-4 break-words"
             data-testid="sa-notice">
          {notice}
        </div>
      )}

      <Section title="Subir una versión"
               subtitle="El navegador calcula la huella del PDF y lo sube por trozos; el servidor la vuelve a calcular antes de guardarlo.">
        <UploadForm kinds={kinds} languages={languages}
                    onUploaded={onUploaded} onError={onUploadError} />
      </Section>

      <Section title="Versiones"
               subtitle="Activar una desactiva la anterior. Verificar comprueba el fichero y lo descarga sin sesión por su URL pública.">
        <div className="dv-card">
          {loading ? (
            <p className="text-sm text-stone-500" data-testid="sa-loading">Cargando…</p>
          ) : items.length === 0 ? (
            <div className="text-sm text-stone-500 py-6 text-center" data-testid="sa-empty">
              <FilePdf size={22} className="mx-auto mb-2 text-stone-400" />
              Todavía no hay ninguna versión. Mientras no haya una activa, no
              se puede entregar ningún dossier.
            </div>
          ) : (
            <ul className="-mx-3 sm:-mx-4" data-testid="sa-list">
              {items.map((a) => (
                <AssetRow key={a.asset_id} asset={a}
                  busy={busyId === a.asset_id}
                  verify={verifications[a.asset_id]}
                  onActivate={onActivate}
                  onDeactivate={onDeactivate}
                  onVerify={onVerify} />
              ))}
            </ul>
          )}
        </div>
      </Section>

      <Section title="Almacenamiento"
               subtitle="Los PDF viven en el disco del servidor, fuera de la copia de seguridad de la base de datos.">
        <StorageCard storage={storage} error={storageError}
                     onRefresh={reloadStorage} busy={busyId === "storage"} />
      </Section>
    </AdminShell>
  );
}
